// ACCEPTANCE — one question in three parts, asked in DEPENDENCY ORDER (tab-acceptance RESEARCH §1):
// can the client actually reach this test plan; what came back broken and how badly; and whose
// court is the ball in right now.
//
// The tab as it stood answered none of the four falsifiable tests of that statement. It listed up
// to seven warning codes and never checked `engagements.portal_enabled` or whether a single portal
// user existed — the two conditions that actually gate reachability, so a clean checklist could
// sit over a plan no client could open. It had no control that raised a defect (`createDefect` had
// zero callers) and none that recorded a run (the endpoint did not exist), so a failure witnessed
// in the room could only be entered by logging in as the client. It never named G5 or G6 — the two
// gates computed exclusively from this tab's own rows. And the operator task DESIGN §5 promises by
// name was fetched by nothing; its only trace was a red badge in a button strip on the third tab.
//
// WHAT IS ON THE PAGE NOW, in order:
//
//   THE LADDER — five rungs in dependency order (REACH · RUN · CLEAR · ISSUED · SIGNED), composed
//   server-side in one snapshot so they cannot be assembled from four responses taken at four
//   moments. Position is the first unmet rung; the one loud rule is drawn on the ALARM when there
//   is one, because a UAT does not run in order.
//
//   THE MOVE — ranked by URGENCY, not by ladder position (RESEARCH T5: an expired window and a
//   plan waiting on the client can be true at the same moment, and a first-unmet-rung rule reads
//   the second one out loud). Emitted in the shared `Move` shape; the lead owns the page's single
//   accent and the rest sit in a disclosure.
//
//   THE READINESS LEDGER — every condition visible, the write attached to the row that names it,
//   and one batch for the five mechanical ones. The two that are JUDGEMENTS — who may see this
//   engagement, and whether an untestable criterion should be tested at all — are excluded from
//   the batch and named as excluded in its preflight.
//
// WHAT SURVIVES UNTOUCHED, because it is the best-reasoned server in the app: the
// deletion-plan-before-the-dialog pattern, `Refresh` recomposing a stale draft while keeping the
// ref, convert-to-CR withdrawing rather than deleting, and `composeAcceptanceRecord`'s hash. The
// document the sponsor signs is not restyled here — only what the page says about it.

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEngagement } from '../context/EngagementContext.js';
import { useT, useLocale } from '../i18n/index.js';
import { tCtx } from '../../shared/i18n.js';
import './AcceptancePage.de.js';
import './AcceptancePage.fr.js';
import { Tx } from '../components/Tx.js';
import { useToast } from '../components/Toast.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState, { ProblemState } from '../components/EmptyState.js';
import { usePoll, POLL_MS } from '../hooks/usePoll.js';
import {
  ROUTES, resolveRouteRef, useDeepLink, useOpenRoute, useRouteParams, scrollToAnchor,
} from '../routes.js';
import type { Problem } from '../../shared/problems.js';
import {
  acceptanceApi,
  type AcceptanceMove, type AcceptanceRecord, type AcceptanceState, type AcceptanceZone,
  type Defect, type DefectSeverity, type DefectStatus, type DeletionPlan,
  type Milestone, type PlanAc, type PlanTestCase, type SeverityDefinitions,
  type TestResult, type TestRun, type UatPlan,
} from '../api/acceptance.js';

// ── vocabulary ─────────────────────────────────────────────────────────────

const OFF_BOARD: DefectStatus[] = ['closed', 'verified', 'withdrawn'];
const DEFECT_STATUSES: DefectStatus[] = ['open', 'in_fix', 'fixed', 'verified', 'closed', 'withdrawn'];
const SEVERITIES: DefectSeverity[] = ['blocking', 'major', 'minor'];

/** The one-step advance the board offers as its filled control. */
const NEXT_STATE: Record<DefectStatus, DefectStatus | null> = {
  open: 'in_fix', in_fix: 'fixed', fixed: 'verified', verified: 'closed', closed: null, withdrawn: null,
};
const STATE_WORD: Record<DefectStatus, string> = {
  open: 'open', in_fix: 'in fix', fixed: 'fixed', verified: 'verified', closed: 'closed', withdrawn: 'withdrawn',
};

/**
 * The two status moves that change whether G6 passes. `gates.ts:417` counts
 * `severity IN ('blocking','major') AND status NOT IN ('closed','verified','withdrawn')`, so
 * moving a defect to verified or closed OPENS A GATE. N6: those get a confirm that says so.
 */
const GATE_MOVING_STATUSES: DefectStatus[] = ['verified', 'closed', 'withdrawn'];

const ZONES: { id: AcceptanceZone; label: string }[] = [
  { id: 'plan', label: 'The plan' },
  { id: 'defects', label: 'Defects' },
  { id: 'record', label: 'The record' },
];

const ANCHOR_ZONE: Record<string, AcceptanceZone> = {
  'ac-readiness': 'plan', 'ac-plan': 'plan', 'ac-runs': 'plan',
  'ac-defects': 'defects', 'ac-records': 'record', 'ac-coverage': 'record',
};

// ── small dress helpers ────────────────────────────────────────────────────

const Ref = ({ children }: { children: React.ReactNode }) => <span className="src-ref">{children}</span>;
const Fig = ({ children }: { children: React.ReactNode }) => <span className="voice-figure">{children}</span>;
const Sep = () => <span className="sep">·</span>;

const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / 86_400_000);

function ResultLamp({ result }: { result: TestResult | null | undefined }) {
  const t = useT();
  if (result === 'pass') return <span className="ac-lamp ac-lamp--pass">{t('pass')}</span>;
  if (result === 'fail') return <span className="ac-lamp ac-lamp--fail">{t('fail')}</span>;
  if (result === 'blocked') return <span className="ac-lamp ac-lamp--blocked">{t('blocked')}</span>;
  return <span className="ac-lamp ac-lamp--none">{t('not run')}</span>;
}

function SeverityChip({ severity }: { severity: DefectSeverity }) {
  const t = useT();
  return <span className={`ac-sev ac-sev--${severity}`}>{t(severity)}</span>;
}

/** The cascade in plain words, for a delete the server has ALLOWED. Kept verbatim (§5). */
function CascadeList({ plan }: { plan: DeletionPlan }) {
  const t = useT();
  if (!plan.cascade.length) {
    return <p className="text-sm text-ink-2">{t('Nothing else points at it — the row goes on its own.')}</p>;
  }
  return (
    <>
      <p className="text-sm text-ink-2">{t('This also removes:')}</p>
      <ul className="text-sm text-ink-2 mt-4" style={{ paddingLeft: 18, listStyle: 'disc' }}>
        {plan.cascade.map((c) => <li key={c.what}><Fig>{c.count}</Fig> {c.what}</li>)}
      </ul>
    </>
  );
}

/**
 * A refused delete. The server's plan carries the blocker's own message and every message names
 * the alternative, so the operator reads what to do instead. No Delete button is rendered at all —
 * a disabled one with no explanation is the failure mode this avoids.
 */
function BlockedDeleteModal({ title, plan, onClose }: { title: string; plan: DeletionPlan; onClose: () => void }) {
  const t = useT();
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="text-sm text-ink-3 mb-12">{plan.label}</p>
          {plan.blockers.map((b) => (
            <div key={b.code} className="card p-12 mb-8" style={{ borderLeft: '2px solid var(--broken)' }}>
              <div className="text-sm text-ink-2">{b.message}</div>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-accent" onClick={onClose}>{t('Understood')}</button>
        </div>
      </div>
    </div>
  );
}

// ── the bundle the page polls ──────────────────────────────────────────────

interface Bundle {
  state: AcceptanceState | null;
  plan: UatPlan | null;
  runs: TestRun[];
  defects: Defect[];
  defectTotals: Record<string, number>;
  severity: SeverityDefinitions | null;
  records: AcceptanceRecord[];
  milestones: Milestone[];
  /** Every source that failed, in ITS OWN words. The state's own problems are merged in. */
  problems: Problem[];
}

const EMPTY_BUNDLE: Bundle = {
  state: null, plan: null, runs: [], defects: [], defectTotals: {}, severity: null,
  records: [], milestones: [], problems: [],
};

// ═══════════════════════════════════════════════════════════════════════════
// The page
// ═══════════════════════════════════════════════════════════════════════════

export default function AcceptancePage() {
  const nav = useEngagement();
  const { current } = nav;
  const openRoute = useOpenRoute(nav);
  const routeParams = useRouteParams();
  const toast = useToast();
  const { t, tp, fmtDate, fmtDateTime } = useLocale();
  const engagementId = current?.id ?? null;

  /** 'YYYY-MM-DD' or an ISO timestamp → the locale's date. Slice first so a timestamp keeps its calendar day. */
  const day = (iso: string | null | undefined): string => fmtDate(iso ? iso.slice(0, 10) : null);

  const [zone, setZone] = useState<AcceptanceZone>('plan');
  const [milestoneId, setMilestoneId] = useState<number | null>(null);

  // ── the one load: five reads, settled independently so one failure never blanks the page ──
  const load = useCallback(async (): Promise<Bundle> => {
    if (!engagementId) return EMPTY_BUNDLE;
    const [state, planRes, runs, defects, records, milestones] = await Promise.allSettled([
      acceptanceApi.state(engagementId),
      acceptanceApi.uatPlan(milestoneId ? { milestone_id: milestoneId } : { engagement_id: engagementId }),
      acceptanceApi.testRuns(engagementId),
      acceptanceApi.defects(engagementId),
      acceptanceApi.list(engagementId),
      acceptanceApi.milestones(engagementId),
    ]);
    const problems: Problem[] = [];
    const take = <T,>(r: PromiseSettledResult<T>, source: string, fallback: T): T => {
      if (r.status === 'fulfilled') return r.value;
      problems.push({ source, message: r.reason instanceof Error ? r.reason.message : String(r.reason) });
      return fallback;
    };
    const st = take(state, 'state', null as AcceptanceState | null);
    const df = take(defects, 'defects', null as Awaited<ReturnType<typeof acceptanceApi.defects>> | null);
    return {
      state: st,
      plan: take(planRes, 'uat-plan', null as UatPlan | null),
      runs: take(runs, 'test-runs', [] as TestRun[]),
      defects: df?.rows ?? [],
      defectTotals: df?.totals ?? {},
      severity: df?.severity ?? null,
      records: take(records, 'acceptance-records', null as Awaited<ReturnType<typeof acceptanceApi.list>> | null)?.rows ?? [],
      milestones: take(milestones, 'milestones', [] as Milestone[]),
      problems: [...problems, ...(st?.problems ?? [])],
    };
  }, [engagementId, milestoneId]);

  const { data, loading, error, notice, refresh } = usePoll(load, POLL_MS);
  const bundle = data ?? EMPTY_BUNDLE;
  const st = bundle.state;

  useDeepLink(routeParams, {
    tab: (v) => { if (v === 'plan' || v === 'defects' || v === 'record') setZone(v); },
  });

  // Filters are page-local and gone on unmount (B4): a new engagement resets the milestone.
  useEffect(() => { setMilestoneId(null); }, [engagementId]);

  const err = useCallback(
    (e: unknown, fallback: string) => toast('err', e instanceof Error ? e.message : fallback),
    [toast],
  );

  // ── writes ───────────────────────────────────────────────────────────────
  const [busy, setBusy] = useState(false);

  // readiness batch
  const [readyOpen, setReadyOpen] = useState(false);
  // defects
  const [raise, setRaise] = useState<{ ac: PlanAc; tc: PlanTestCase | null; runId: number | null } | null>(null);
  const [raiseTitle, setRaiseTitle] = useState('');
  const [raiseDesc, setRaiseDesc] = useState('');
  const [raiseSeverity, setRaiseSeverity] = useState<DefectSeverity>('major');
  const [raiseFix, setRaiseFix] = useState('');
  const [advance, setAdvance] = useState<{ defect: Defect; next: DefectStatus } | null>(null);
  const [severityChange, setSeverityChange] = useState<{ defect: Defect; next: DefectSeverity } | null>(null);
  const [convertTarget, setConvertTarget] = useState<Defect | null>(null);
  const [convertNote, setConvertNote] = useState('');
  const [defectDelete, setDefectDelete] = useState<{ defect: Defect; plan: DeletionPlan } | null>(null);
  const [defectDeleteBusy, setDefectDeleteBusy] = useState(false);
  // runs
  const [recordRun, setRecordRun] = useState<{ tc: PlanTestCase; result: TestResult } | null>(null);
  const [runNote, setRunNote] = useState('');
  // records
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMilestone, setComposerMilestone] = useState<number | null>(null);
  const [composerDeliverables, setComposerDeliverables] = useState<number[]>([]);
  const [composerOptions, setComposerOptions] = useState<{ id: number; ref: string; name: string; milestone_name: string | null }[]>([]);
  const [exclusions, setExclusions] = useState('');
  const [previewWarnings, setPreviewWarnings] = useState<string[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [viewRecord, setViewRecord] = useState<AcceptanceRecord | null>(null);
  const [issueTarget, setIssueTarget] = useState<AcceptanceRecord | null>(null);
  const [issueDeadline, setIssueDeadline] = useState('');
  const [noticeTarget, setNoticeTarget] = useState<AcceptanceRecord | null>(null);
  const [noticeNote, setNoticeNote] = useState('');
  const [withdrawTarget, setWithdrawTarget] = useState<AcceptanceRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AcceptanceRecord | null>(null);
  const [previewAsClient, setPreviewAsClient] = useState(false);

  // The composer opens on the milestone the operator is most likely to be closing — the first
  // that is not already accepted. Without this it opens on "— no milestone —", and a record
  // composed there carries NO deliverables: `resolveDeliverableIds` has nothing to resolve and
  // the document prints "No deliverables are attached to this record."
  useEffect(() => { setComposerMilestone(null); }, [engagementId]);
  useEffect(() => {
    if (composerMilestone !== null || !bundle.milestones.length) return;
    setComposerMilestone(bundle.milestones.find((m) => m.status !== 'accepted')?.id ?? bundle.milestones[0]!.id);
  }, [bundle.milestones, composerMilestone]);

  const closeLayers = useCallback(() => {
    setRaise(null); setComposerOpen(false);
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeLayers(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeLayers]);

  // ── navigation out of a server-emitted route ─────────────────────────────
  const follow = useCallback(
    (route: Record<string, unknown> | null) => {
      const r = resolveRouteRef(route);
      if (r.kind === 'in-page') {
        const target = ANCHOR_ZONE[r.anchor];
        if (target) setZone(target);
        scrollToAnchor(r.anchor);
        return;
      }
      if (r.kind === 'dest') { void openRoute(r.dest); return; }
      if (r.kind === 'client-view') { void openRoute(ROUTES.clients()); return; }
      // kind:'none' carries a sentence. Show it; never swallow it.
      toast('warn', r.reason);
    },
    [openRoute, toast],
  );

  // ── derived, from the plan the zone renders ──────────────────────────────
  /** test_case_id → its acceptance criterion, so a failing RUN can name what it violates. */
  const acOfTestCase = useMemo(() => {
    const map = new Map<number, PlanAc>();
    for (const d of bundle.plan?.plan ?? []) {
      for (const r of d.requirements) for (const ac of r.acceptance_criteria) {
        for (const tc of ac.test_cases) map.set(tc.id, ac);
      }
    }
    return map;
  }, [bundle.plan]);

  const testCaseById = useMemo(() => {
    const map = new Map<number, PlanTestCase>();
    for (const d of bundle.plan?.plan ?? []) {
      for (const r of d.requirements) for (const ac of r.acceptance_criteria) {
        for (const tc of ac.test_cases) map.set(tc.id, tc);
      }
    }
    return map;
  }, [bundle.plan]);

  /** Runs that already produced a defect. A second "Raise defect" on the same run is a duplicate. */
  const runsWithDefect = useMemo(
    () => new Set(bundle.defects.map((d) => d.test_run_id).filter((id): id is number => id !== null)),
    [bundle.defects],
  );

  // ── mutations ────────────────────────────────────────────────────────────

  const patchDefect = async (d: Defect, body: Parameters<typeof acceptanceApi.updateDefect>[1], say: string) => {
    try {
      await acceptanceApi.updateDefect(d.id, body);
      toast('ok', say);
      await refresh();
    } catch (e) { err(e, t('Could not update the defect')); }
  };

  const doReadyPlan = async (wants: Parameters<typeof acceptanceApi.planReady>[0]) => {
    setBusy(true);
    try {
      const out = await acceptanceApi.planReady(wants);
      toast('ok', out.notice);
      // Every refusal is stated — nothing is silently skipped (F7.1).
      out.refused.forEach((r) => toast('warn', r.reason));
      setReadyOpen(false);
      await refresh();
    } catch (e) { err(e, t('The batch could not be written')); } finally { setBusy(false); }
  };

  const doRaise = async () => {
    if (!raise) return;
    setBusy(true);
    try {
      await acceptanceApi.createDefect({
        acceptance_criterion_id: raise.ac.id,
        severity: raiseSeverity,
        title: raiseTitle,
        description: raiseDesc || undefined,
        test_run_id: raise.runId ?? undefined,
        fix_due_date: raiseFix || undefined,
      });
      toast('ok', t('Defect raised against {ref}', { ref: raise.ac.ref }));
      setRaise(null); setRaiseTitle(''); setRaiseDesc(''); setRaiseFix(''); setRaiseSeverity('major');
      setZone('defects');
      await refresh();
    } catch (e) { err(e, t('Could not raise the defect')); } finally { setBusy(false); }
  };

  const doRecordRun = async () => {
    if (!recordRun) return;
    setBusy(true);
    try {
      const out = await acceptanceApi.recordRun({
        test_case_id: recordRun.tc.id, result: recordRun.result, note: runNote || undefined,
      });
      toast('ok', out.notice);
      setRecordRun(null); setRunNote('');
      await refresh();
    } catch (e) { err(e, t('Could not record the run')); } finally { setBusy(false); }
  };

  const askDeleteDefect = async (d: Defect) => {
    try {
      setDefectDelete({ defect: d, plan: await acceptanceApi.defectDeletionPlan(d.id) });
    } catch (e) { err(e, t('Could not work out what deleting this defect would do')); }
  };

  const doDeleteDefect = async () => {
    if (!defectDelete) return;
    const { defect } = defectDelete;
    setDefectDeleteBusy(true);
    try {
      await acceptanceApi.deleteDefect(defect.id);
      toast('ok', t('{ref} deleted', { ref: defect.ref }));
      setDefectDelete(null);
      await refresh();
    } catch (e) {
      // The DELETE recomputes the plan server-side — it never trusts the dialog's copy. If it
      // refuses now, re-read the plan and show the refusal with the alternative it names.
      err(e, t('Could not delete the defect'));
      try {
        setDefectDelete({ defect, plan: await acceptanceApi.defectDeletionPlan(defect.id) });
      } catch { setDefectDelete(null); }
    } finally { setDefectDeleteBusy(false); }
  };

  const runConvert = async () => {
    if (!convertTarget) return;
    try {
      const out = await acceptanceApi.convertDefectToCr(convertTarget.id, convertNote || undefined);
      toast('ok', out.notice);
      setConvertTarget(null); setConvertNote('');
      await refresh();
    } catch (e) { err(e, t('Could not convert the defect')); }
  };

  /**
   * The composer's checklist comes from the preview call (every deliverable in the engagement),
   * NOT from the plan zone's filtered list — those are scoped to a different milestone and would
   * offer the wrong set.
   */
  const doPreview = useCallback(async () => {
    if (!engagementId) return;
    setBusy(true); setPreviewError(null);
    try {
      const out = await acceptanceApi.preview({
        engagement_id: engagementId,
        milestone_id: composerMilestone,
        deliverable_ids: composerDeliverables.length ? composerDeliverables : undefined,
        exclusions_note: exclusions || undefined,
      });
      setPreviewWarnings(out.warnings);
      setComposerOptions(out.available_deliverables ?? []);
      if (!composerDeliverables.length) setComposerDeliverables(out.deliverable_ids);
    } catch (e) {
      // The old page disabled `Create draft` on a failed preview with no explanation — the exact
      // failure its own delete path was built to avoid. Now the refusal is on the page.
      setPreviewError(e instanceof Error ? e.message : t('The preview could not be rendered.'));
    } finally { setBusy(false); }
  }, [engagementId, composerMilestone, composerDeliverables, exclusions, t]);

  const doCreate = async () => {
    if (!engagementId) return;
    setBusy(true);
    try {
      const ar = await acceptanceApi.create({
        engagement_id: engagementId,
        milestone_id: composerMilestone,
        deliverable_ids: composerDeliverables.length ? composerDeliverables : undefined,
        exclusions_note: exclusions || undefined,
      });
      toast('ok', t('{ref} composed as a draft', { ref: ar.ref }));
      setComposerOpen(false); setPreviewWarnings(null); setExclusions(''); setComposerDeliverables([]);
      setZone('record');
      await refresh();
    } catch (e) { err(e, t('Could not compose the record')); } finally { setBusy(false); }
  };

  const doIssue = async () => {
    if (!issueTarget) return;
    try {
      const out = await acceptanceApi.issue(issueTarget.id, issueDeadline || undefined);
      toast('ok', out.notice);
      (out.warnings ?? []).forEach((w) => toast('warn', w));
      setIssueTarget(null); setIssueDeadline('');
      await refresh();
    } catch (e) { err(e, t('Could not issue the record')); }
  };

  const doNotice = async () => {
    if (!noticeTarget) return;
    try {
      const out = await acceptanceApi.deemedNotice(noticeTarget.id, noticeNote || undefined);
      toast('ok', out.notice ?? t('Deemed-acceptance notice issued on {ref}', { ref: out.ref }));
      setNoticeTarget(null); setNoticeNote('');
      await refresh();
    } catch (e) { err(e, t('Could not issue the notice')); }
  };

  /** A draft freezes at composition. Recompose it against the current facts, keeping the ref. */
  const doRefreshRecord = async (ar: AcceptanceRecord) => {
    setBusy(true);
    try {
      const out = await acceptanceApi.refresh(ar.id);
      toast('ok', out.notice);
      (out.warnings ?? []).forEach((w) => toast('warn', w));
      await refresh();
      if (viewRecord?.id === ar.id) setViewRecord(await acceptanceApi.get(ar.id));
    } catch (e) { err(e, t('Could not recompose the record')); } finally { setBusy(false); }
  };

  const doWithdrawRecord = async () => {
    if (!withdrawTarget) return;
    try {
      const out = await acceptanceApi.withdraw(withdrawTarget.id);
      toast('ok', t('{ref} withdrawn — unpublished from the portal', { ref: out.ref }));
      setWithdrawTarget(null);
      if (viewRecord?.id === out.id) setViewRecord(null);
      await refresh();
    } catch (e) { err(e, t('Could not withdraw the record')); setWithdrawTarget(null); }
  };

  const doDeleteRecord = async () => {
    if (!deleteTarget) return;
    try {
      await acceptanceApi.remove(deleteTarget.id);
      toast('ok', t('Draft {ref} discarded', { ref: deleteTarget.ref }));
      if (viewRecord?.id === deleteTarget.id) setViewRecord(null);
      setDeleteTarget(null);
      await refresh();
    } catch (e) { err(e, t('Could not delete the draft')); setDeleteTarget(null); }
  };

  // ── no engagement ────────────────────────────────────────────────────────
  if (!current) {
    return (
      <div className="page ac">
        <header className="mast"><div className="mast-top"><div className="mast-main"><h1>{t('Acceptance')}</h1><div className="mast-sub">{t('no engagement selected')}</div></div></div></header>
        <div style={{ marginTop: 24 }}>
          <EmptyState
            kind="empty"
            title={t('UAT and acceptance are always scoped to one engagement')}
            detail={t('A test plan belongs to one register and one client portal. Pick an engagement in the top bar and its instrument loads here.')}
          />
        </div>
      </div>
    );
  }

  // ── the whole instrument failed and nothing has ever loaded ──────────────
  if (!st && !loading) {
    return (
      <div className="page ac">
        <header className="mast">
          <div className="mast-top"><div className="mast-main"><h1>{t('Acceptance')}</h1><div className="mast-sub"><Ref>{current.code}</Ref></div></div></div>
        </header>
        <div style={{ marginTop: 24 }}>
          <EmptyState
            kind="error"
            title={t('The acceptance instrument could not be read')}
            detail={error ?? bundle.problems[0]?.message ?? t('GET /api/acceptance/state did not answer.')}
            onRetry={() => void refresh()}
          />
        </div>
      </div>
    );
  }

  const rail = st?.rail ?? [];
  const position = st?.position ?? 0;
  const anyAlarm = rail.some((r) => r.alarm);
  const lead = st?.moves[0] ?? null;
  const alsoStanding = st?.moves.slice(1) ?? [];

  const gateLine = (['G5', 'G6'] as const).map((g) => st?.gates[g] ?? null);
  const firstUnmetCondition = [st?.gates.G5, st?.gates.G6]
    .flatMap((g) => g?.conditions ?? [])
    .find((c) => !c.met) ?? null;

  const runnable = st?.reachability.test_cases.runnable ?? 0;
  const tcTotal = st?.reachability.test_cases.total ?? 0;
  const conditions = st?.reachability.conditions ?? [];
  const unmetConditions = conditions.filter((c) => !c.done);
  const batchRows = unmetConditions.filter((c) => !c.soft);
  const heldRows = unmetConditions.filter((c) => c.soft);
  /** Every non-dropped deliverable in the engagement — the coverage strip's universe. */
  const engagementDeliverables = st?.records.coverage.length ?? 0;

  return (
    <div className="page ac">

      {/* ══ masthead ══ */}
      <header className="mast">
        <div className="mast-top">
        <div className="mast-main">
          <h1>{t('Acceptance')}</h1>
          <div className="mast-sub">
            <Ref>{current.code}</Ref>
            <Sep />
            <span>{st?.engagement.client_name ?? current.client_name ?? '—'}</span>
            <Sep />
            <span>{st?.engagement.name ?? current.name}</span>
            <Sep />
            <span>{t('review window')} <Fig>{st?.engagement.review_window_days ?? '—'}</Fig> {t('working days')}</span>
          </div>
        </div>
        <div className="mast-verbs">
          {notice && <span className="ac-notice">{notice}</span>}
          <button className="btn" onClick={() => setPreviewAsClient(true)}>{t('Preview as client')}</button>
        </div>
        </div>
      </header>

      {/* ══ the ladder — five rungs in dependency order, on the instrument sheet ══ */}
      <div className="instr">
      <div className="rail ac-ladder">
        {rail.map((cell, i) => {
          const state = cell.met ? 'met' : i === position ? (anyAlarm && !cell.alarm ? 'position' : 'current') : 'ahead';
          const cls = `fld ac-rung ac-rung--${state}${cell.alarm ? ' ac-rung--alarm' : ''}${cell.warn ? ' ac-rung--warn' : ''}`;
          return (
            <button key={cell.key} className={cls} onClick={() => follow(cell.route)} title={cell.detail}>
              <div className="step">
                <span className="step-n">{cell.step}</span>
                <span className="u-label">{cell.key}</span>
                <span className={`ac-tick ${cell.met ? 'ac-tick--met' : 'ac-tick--open'}`}>{cell.met ? '✓' : '·'}</span>
              </div>
              <div className="val voice-figure">
                <span className={cell.tone === 'unknown' ? 'n-unknown' : cell.tone === 'nil' ? 'n-none' : undefined}>
                  {cell.value}
                </span>
                <span className="unit">{cell.unit}</span>
              </div>
              <div className="det">{cell.detail}</div>
            </button>
          );
        })}
      </div>
      </div>

      <div className="ac-ladder-foot">
        <span className="lead">
          {position >= rail.length ? t('Everything on this tab is discharged.') : t('Step {n} of {total}.', { n: position + 1, total: rail.length })}
        </span>
        {st?.ladder_note && <span>{st.ladder_note}</span>}
        <span>
          {t('The two gates this tab clears:')}{' '}
          {gateLine.map((g, i) => (
            <span key={g?.gate ?? i}>
              {i > 0 && <> <Sep /> </>}
              {g
                ? <><Ref>{g.gate}</Ref> <Fig>{g.met_count}</Fig> {t('of')} <Fig>{g.total}</Fig></>
                : <span className="n-unknown" title={t('the gate engine did not answer')}>{i === 0 ? 'G5' : 'G6'} —</span>}
            </span>
          ))}.
        </span>
        {firstUnmetCondition && <span>{t('First unmet — {label}: {detail}.', { label: firstUnmetCondition.label, detail: firstUnmetCondition.detail })}</span>}
        <button className="ac-linkbtn" onClick={() => void openRoute(ROUTES.lifecycle({ engagement: current.id }))}>
          {t('Lifecycle →')}
        </button>
      </div>

      <ProblemState
        problems={bundle.problems}
        source="state"
        zone={t('The acceptance instrument')}
        onRetry={() => void refresh()}
      />

      {/* ══ the next move — ranked by urgency, not by ladder position ══ */}
      {lead && (
        <div className={`ac-move${lead.tone === 'err' ? ' ac-move--alarm' : lead.tone === 'warn' ? ' ac-move--warn' : ''}`}>
          <div className="u-label" style={{ marginBottom: 7 }}>{t('Next move')}</div>
          <h2>{lead.title}</h2>
          <p className="why">{lead.why}</p>
          <div className="acts">
            {moveControls(lead)}
          </div>
          {alsoStanding.length > 0 && (
            <details className="ac-move-also">
              <summary>{tp(alsoStanding.length, '{n} other thing standing on this tab', '{n} other things standing on this tab')}</summary>
              {alsoStanding.map((m) => (
                <div className="row" key={m.rule}>
                  <b>{m.title}</b> {m.why}
                </div>
              ))}
            </details>
          )}
        </div>
      )}

      {/* ══ zones ══ */}
      <div className="ac-tabs" role="tablist">
        {ZONES.map((z) => (
          <button
            key={z.id} role="tab" aria-selected={zone === z.id}
            onClick={() => setZone(z.id)}
          >
            {t(z.label)}
            {z.id === 'defects' && (st?.defects.blocking_open ?? 0) > 0 && <span className="flag" style={{ color: 'var(--broken)' }}>●</span>}
            {z.id === 'defects' && (st?.defects.blocking_open ?? 0) === 0 && (st?.defects.major_open ?? 0) > 0 && <span className="flag" style={{ color: 'var(--attend)' }}>●</span>}
            {z.id === 'record' && st?.records.clock?.window_expired && <span className="flag" style={{ color: 'var(--broken)' }}>●</span>}
          </button>
        ))}
      </div>

      {zone === 'plan' && renderPlanZone()}
      {zone === 'defects' && renderDefectZone()}
      {zone === 'record' && renderRecordZone()}

      {/* ══ floating layers ══ */}
      {renderComposer()}
      {renderRaisePanel()}
      {renderModals()}
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // The move's controls. Each rule's control is the one thing that discharges it.
  // ═════════════════════════════════════════════════════════════════════════
  function moveControls(m: AcceptanceMove) {
    switch (m.rule) {
      case 'unreachable':
        return (
          <>
            <button className="btn btn-accent" onClick={() => setReadyOpen(true)}>{t('Ready the plan')}</button>
            <span className="aside">{t('names every write, and every condition it will not touch, before it makes any')}</span>
          </>
        );
      case 'expired':
        return (
          <>
            <button
              className="btn btn-accent"
              onClick={() => {
                const ar = bundle.records.find((r) => r.id === st?.records.clock?.id);
                if (ar) { setNoticeTarget(ar); setNoticeNote(''); } else { setZone('record'); }
              }}
            >{t('Issue the deemed-acceptance notice')}</button>
            <button className="btn" onClick={() => follow(m.route)}>{t('Read the record first')}</button>
            {st?.tasks[0] && (
              <span className="aside">
                {t("from the scheduler's task on")} <Ref>{String(st.tasks[0].related?.ar_ref ?? st.records.clock?.ref ?? '')}</Ref>,
                {' '}{tCtx(t, 'raised', 'ago')} <Fig>{st.tasks[0].age_days}</Fig> {t('days ago')}
              </span>
            )}
          </>
        );
      case 'blocking':
      case 'major': {
        const d = bundle.defects.find((x) => x.ref === (m.refs ?? [])[0]);
        const next = d ? NEXT_STATE[d.status] : null;
        return (
          <>
            {d && next && (
              <button className="btn btn-accent" onClick={() => setAdvance({ defect: d, next })}>
                {t('Move {ref} to {state}', { ref: d.ref, state: t(STATE_WORD[next]) })}
              </button>
            )}
            <button className="btn" onClick={() => follow(m.route)}>{t('Open the board')}</button>
          </>
        );
      }
      case 'compose':
        return (
          <button className="btn btn-accent" onClick={() => { setComposerOpen(true); void doPreview(); }}>
            {t('Compose a record')}
          </button>
        );
      case 'no-plan':
        return (
          <>
            {/* The server's RouteRef carries the screen; the TAB is client knowledge, so the
                destination is composed here rather than smuggled through resolveRouteRef. */}
            <button
              className="btn btn-accent"
              onClick={() => void openRoute(ROUTES.register({ engagement: current!.id, tab: 'test-cases' }))}
            >{t('Write the test cases →')}</button>
            <span className="aside">{t("the Register's test-case tab, where a criterion becomes something a person can run")}</span>
          </>
        );
      case 'waiting':
        return <button className="btn" onClick={() => follow(m.route)}>{t('See the plan')}</button>;
      default:
        return <button className="btn" onClick={() => follow(m.route)}>{t('Open the record')}</button>;
    }
  }

  // ═════════════════════════════════════════════════════════════════════════
  // ZONE · THE PLAN
  // ═════════════════════════════════════════════════════════════════════════
  function renderPlanZone() {
    const plan = bundle.plan;
    // ONE FACT ONCE. When NOTHING on the plan is reachable, every row carries the same blockers
    // and the ledger above has already said them — so the rows stay quiet and one hoisted line
    // states it. (URSC is the live case: 34 criteria, zero test cases, and the old page put an
    // amber chip on all 34 saying so.)
    const allBlocked = runnable === 0;
    const dels = plan?.plan ?? [];

    return (
      <div className="ac-zone">
        {/* ── the readiness ledger ── */}
        <div id="ac-readiness" data-anchor="ac-readiness">
          {conditions.length === 0 ? null : unmetConditions.length === 0 ? (
            <div className="ac-ledger">
              <div className="ac-lrow ac-lrow--done">
                <div>
                  <div className="what">
                    <span className="ac-mark ac-mark--done">✓</span>
                    {t('The plan is reachable —')} <Fig>{tcTotal}</Fig> {t('test cases, published and live for {client}.', { client: st?.engagement.client_name })}
                  </div>
                  <div className="objs">
                    {t('Portal on')} <Sep /> <Fig>{st?.reachability.portal_users}</Fig> {t('invited')} <Sep /> {t('matrix open on test cases, criteria and defects.')}
                  </div>
                </div>
                <div><button className="btn btn-sm" onClick={() => setPreviewAsClient(true)}>{t('Preview as client')}</button></div>
              </div>
            </div>
          ) : (
            <div className="ac-ledger">
              <div className="ac-ledger-head">
                <h3>{t('Before the client can run this')}</h3>
                <span className="count">
                  <Fig>{unmetConditions.length}</Fig> {t('of')} <Fig>{conditions.length}</Fig> {t('conditions unmet')}
                </span>
              </div>
              {conditions.map((c) => (
                <div className={`ac-lrow${c.done ? ' ac-lrow--done' : ''}`} key={c.id}>
                  <div>
                    <div className="what">
                      {/* B8: a condition met over an EMPTY universe never wears a tick. */}
                      <span className={`ac-mark ${c.applicable === false ? '' : c.done ? 'ac-mark--done' : 'ac-mark--blocked'}`}>
                        {c.applicable === false ? '—' : c.done ? '✓' : '·'}
                      </span>
                      {c.what}
                    </div>
                    <div className="objs">
                      {c.applicable === false ? <span className="n-none">{c.vacuous_reason}</span> : c.objects}
                    </div>
                  </div>
                  <div>
                    {c.applicable === false ? (
                      <span className="ac-tag n-unknown" title={c.vacuous_reason}>{t('not applicable')}</span>
                    ) : c.done ? (
                      <span className="ac-tag">{t('clear')}</span>
                    ) : c.route ? (
                      <button className="btn btn-sm" onClick={() => follow(c.route)}>{c.action}</button>
                    ) : (
                      <button
                        className="btn btn-sm"
                        disabled={busy}
                        onClick={() => void doReadyPlan({
                          engagement_id: current!.id,
                          open_matrix: c.id === 'matrix',
                          mark_ready: c.id === 'tc_ready',
                          publish_test_cases: c.id === 'tc_published',
                          publish_acs: c.id === 'ac_published',
                          publish_milestones: c.id === 'milestone',
                        })}
                        title={c.writes}
                      >{c.action}</button>
                    )}
                  </div>
                </div>
              ))}
              <div className="ac-ledger-foot">
                <button className="btn" disabled={busy || batchRows.length === 0} onClick={() => setReadyOpen(true)}>
                  {t('Ready the plan')}
                </button>
                <span className="note">
                  {t('The same control as the card above — one confirm,')} <Fig>{batchRows.length}</Fig> {t('writes, no page changes. The portal switch and the linter row are not in it: who may see this engagement, and whether a criterion is testable at all, are judgements, not flags a batch can flip.')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ── the plan itself ── */}
        <div className="zone ac-sec" id="ac-plan" data-anchor="ac-plan">
          <div className="zone-head">
            <span className="u-label">{t('The plan')}</span>
            <span className="zone-note zone-note--end">
              <Fig>{tcTotal}</Fig> {t('test cases')} <Sep /> <Fig>{runnable}</Fig> {t('runnable')} <Sep />{' '}
              <Fig>{st?.runs.recorded ?? 0}</Fig> {tCtx(t, 'run', 'done')} <Sep /> <Fig>{st?.runs.pass ?? 0}</Fig> {t('pass')} <Sep />{' '}
              <Fig>{st?.runs.fail ?? 0}</Fig> {t('fail')}
            </span>
          </div>

          <div className="ac-seg">
            {/* The engagement's own count, not the sum over milestones: a deliverable mapped to no
                milestone is still in the engagement, and URSC's five are exactly that. */}
            <button aria-pressed={milestoneId === null} onClick={() => setMilestoneId(null)}>
              {t('Everything in the engagement')} <Sep /> <Fig>{tp(engagementDeliverables, '{n} deliverable', '{n} deliverables')}</Fig>
            </button>
            {bundle.milestones.map((m) => (
              <button key={m.id} aria-pressed={milestoneId === m.id} onClick={() => setMilestoneId(m.id)}>
                {m.name} <Sep /> <Fig>{tp(m.deliverable_count, '{n} deliverable', '{n} deliverables')}</Fig>
              </button>
            ))}
          </div>

          {loading && !plan ? (
            <div className="spinner" />
          ) : dels.length === 0 ? (
            // RESEARCH T5: an empty state must say whether it describes the FILTER or the register.
            milestoneId !== null ? (
              <EmptyState
                kind="no-match"
                title={t('This milestone carries no deliverables')}
                detail={
                  <>
                    {t('“{name}” has nothing mapped to it, so there is nothing to test', { name: bundle.milestones.find((m) => m.id === milestoneId)?.name ?? t('This milestone') })}
                    {' '}<em>{t('under this filter')}</em>{t('. The engagement has')}{' '}
                    <Fig>{tp(engagementDeliverables, '{n} deliverable', '{n} deliverables')}</Fig>{' '}
                    {t('elsewhere and')} <Fig>{tp(bundle.runs.length, '{n} recorded run', '{n} recorded runs')}</Fig> {t('— the filter is hiding them, not the register.')}
                  </>
                }
                onClear={() => setMilestoneId(null)}
                clearLabel={t('Show everything in the engagement')}
              />
            ) : (
              <EmptyState
                kind="empty"
                title={t('Nothing to test yet')}
                detail={
                  <>
                    {t('A plan needs a deliverable, a requirement mapped to it, an acceptance criterion on that requirement and a test case against the criterion. This engagement has')}{' '}
                    <Fig>{st?.reachability.acceptance_criteria.total ?? 0}</Fig> {t('acceptance criteria and')}{' '}
                    <Fig>{tcTotal}</Fig> {t('test cases on record.')}
                  </>
                }
              >
                <button className="btn btn-sm" onClick={() => void openRoute(ROUTES.register({ engagement: current!.id, tab: 'test-cases' }))}>
                  {t('Open the Register →')}
                </button>
              </EmptyState>
            )
          ) : (
            dels.map((d) => (
              <div className="ac-del" key={d.id}>
                <div className="ac-del-head">
                  <h3><Ref>{d.ref}</Ref> {d.name}</h3>
                  <div className="meta">
                    <span>{t(d.status)}</span>
                    <Sep />
                    {/* `published` carries no hue — its ABSENCE is the signal (LAW 2). */}
                    {d.published ? <span>{t('published')}</span> : <span className="ac-tag ac-tag--attend">{t('unpublished')}</span>}
                  </div>
                </div>
                {d.description && <div className="ac-dod">{d.description}</div>}
                {allBlocked && (
                  <div className="ac-hoist">
                    {t('None of these criteria is reachable by {client} — the reasons are the', { client: st?.engagement.client_name })}{' '}
                    <Fig>{unmetConditions.length}</Fig> {t('conditions above, not anything on these rows.')}
                    {tcTotal === 0 && ` ${t('No test case exists on this engagement at all, so there is nothing for the client to press pass or fail on.')}`}
                    {' '}{t('Read them here as the register, not as a plan that is live.')}
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  {d.requirements.length === 0 ? (
                    <div className="ac-crit"><div className="ids" /><div className="stmt" style={{ fontFamily: 'var(--sans)', fontSize: 'var(--t-scan)', color: 'var(--ink-3)' }}>
                      {t('No requirement is mapped to this deliverable — unjustified work.')}
                    </div><div /></div>
                  ) : (
                    d.requirements.flatMap((r) =>
                      r.acceptance_criteria.length === 0
                        ? [(
                            <div className="ac-crit" key={`r${r.id}`}>
                              <div className="ids">
                                <div className="line">
                                  <span className={`att ${['confirmed', 'baselined', 'changed'].includes(r.status) ? 'att--held' : 'att--proposed'}`} />
                                  <Ref>{r.ref}</Ref>
                                </div>
                              </div>
                              <div className="stmt" style={{ fontFamily: 'var(--sans)', fontSize: 'var(--t-scan)', color: 'var(--ink-3)' }}>
                                {t('No acceptance criterion — nothing to test, and nothing a defect could cite.')}
                              </div>
                              <div />
                            </div>
                          )]
                        : r.acceptance_criteria.map((ac) => renderCriterion(r, ac, allBlocked)),
                    )
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── the recorded runs ── */}
        <div className="zone ac-sec" id="ac-runs" data-anchor="ac-runs">
          <div className="zone-head">
            <span className="u-label">{t('Recorded runs')}</span>
            <span className="zone-note zone-note--end">
              {bundle.runs.length
                ? <><Fig>{bundle.runs.length}</Fig> {t('recorded')} <Sep /> {t('latest')} <Fig>{fmtDateTime(bundle.runs[0]?.run_at)}</Fig></>
                : <span className="n-none">{t('nothing recorded')}</span>}
            </span>
          </div>
          {bundle.runs.length === 0 ? (
            <EmptyState
              kind="empty"
              title={t('No result has been recorded')}
              detail={
                runnable === tcTotal && tcTotal > 0
                  ? t('Results land here the moment the client records them in the portal — or the moment you record one yourself from a witnessed session, using the control on each criterion above.')
                  : t('Results land here the moment the client records them in the portal — or the moment you record one yourself from a witnessed session. Right now neither can happen: the plan is not reachable, so the portal shows the client nothing to run.')
              }
            >
              <button className="btn btn-sm" onClick={() => scrollToAnchor('ac-readiness')}>{t('Back to step 1')}</button>
            </EmptyState>
          ) : (
            <div className="ac-scroll">
              <table className="tbl ac-t">
                <thead>
                  <tr>
                    <th style={{ width: 88 }}>{t('Test')}</th>
                    <th>{t('Note the tester left')}</th>
                    <th style={{ width: 96 }}>{t('Result')}</th>
                    <th style={{ width: 190 }}>{t('By')}</th>
                    <th style={{ width: 150 }}>{t('When')}</th>
                    <th style={{ width: 130 }}>{t('Evidence')}</th>
                  </tr>
                </thead>
                <tbody>
                  {bundle.runs.map((run) => {
                    const ac = acOfTestCase.get(run.test_case_id) ?? null;
                    return (
                      <tr key={run.id}>
                        <td><Ref>{run.test_case_ref}</Ref></td>
                        <td>
                          <Tx type="test_case" id={run.test_case_id} field="title" original={run.test_case_title}
                              style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-3)' }} as="div" />
                          {run.note && <div className="voice-record" style={{ marginTop: 4, fontSize: 'var(--t-scan)' }}>{run.note}</div>}
                          {run.defects.length > 0 && (
                            <div style={{ marginTop: 5, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                              {run.defects.map((d) => <span key={d.id} className="ac-lamp ac-lamp--fail">{d.ref}</span>)}
                            </div>
                          )}
                          {run.result === 'fail' && !run.defects.length && ac && (
                            <div style={{ marginTop: 5 }}>
                              <button
                                className="btn btn-sm"
                                onClick={() => openRaise(ac, testCaseById.get(run.test_case_id) ?? null, run.id, run.note)}
                              >{t('Raise defect')}</button>
                            </div>
                          )}
                        </td>
                        <td><ResultLamp result={run.result} /></td>
                        <td>
                          {run.run_by ?? '—'}
                          <div style={{ fontSize: 'var(--t-label)', color: 'var(--ink-3)' }}>
                            {t(run.run_by_kind)}{run.portal_role ? ` · ${run.portal_role}` : ''}
                          </div>
                        </td>
                        <td className="voice-figure" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-3)' }}>{fmtDateTime(run.run_at)}</td>
                        <td>
                          {run.has_attachment
                            ? <a className="btn btn-sm" href={acceptanceApi.attachmentUrl(run.id)} download>{t('attachment')}</a>
                            : <span className="n-none">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  /** One criterion: its ids, the statement in the RECORD's voice, and its run cell. */
  function renderCriterion(r: UatPlan['plan'][number]['requirements'][number], ac: PlanAc, allBlocked: boolean) {
    const tc = ac.test_cases[0] ?? null;
    const run = tc?.latest_run ?? null;
    const lintErrors = ac.lint_flags && !ac.lint_flags.pass
      ? ac.lint_flags.flags.filter((f) => f.severity === 'error')
      : [];
    // ONE FACT ONCE: when EVERY row carries the same blockers the ledger above already says it,
    // so the rows stay quiet. Chips appear only where a row differs.
    const chips: string[] = [];
    if (!allBlocked) {
      if (tc && tc.status !== 'ready') chips.push('draft');
      if (tc && !tc.published) chips.push('unpublished');
      if (!ac.published) chips.push('criterion hidden');
    }
    return (
      <div className={`ac-crit${run?.result === 'fail' ? ' ac-crit--fail' : ''}`} key={ac.id}>
        <div className="ids">
          <div className="line">
            <span className={`att ${['confirmed', 'baselined', 'changed'].includes(r.status) ? 'att--held' : 'att--proposed'}`} />
            <Ref>{r.ref}</Ref>
          </div>
          <div className="line">
            <span className={`att ${ac.status === 'draft' ? 'att--proposed' : 'att--held'}`} />
            <Ref>{ac.ref}</Ref>
          </div>
          <div className="line" style={{ color: 'var(--ink-4)' }}>
            <Ref>{tc ? tc.ref : t('no test')}</Ref>
          </div>
        </div>
        <div>
          <Tx type="acceptance_criterion" id={ac.id} field="statement" original={ac.statement} className="stmt" as="div" />
          {lintErrors.length > 0 && (
            <div className="lint"><Ref>lint</Ref> {lintErrors.map((f) => f.message).join(' ')}</div>
          )}
          <div style={{ marginTop: 7, fontSize: 'var(--t-meta)', color: 'var(--ink-3)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }}>
            {tc
              ? <Tx type="test_case" id={tc.id} field="title" original={tc.title} as="span" />
              : <span>—</span>}
            {chips.map((c) => <span key={c} className="ac-tag ac-tag--attend">{t(c)}</span>)}
          </div>
        </div>
        <div className="runcell">
          <div className="top"><ResultLamp result={run?.result} /></div>
          {run
            ? <div className="when"><span className="voice-figure">{fmtDateTime(run.run_at)}</span><br />{run.run_by ?? '—'}</div>
            : <div className="when n-none">{t('— nothing recorded')}</div>}
          {run?.result === 'fail' && !runsWithDefect.has(run.id) && (
            <div style={{ marginTop: 6 }}>
              <button className="btn btn-sm" onClick={() => openRaise(ac, tc, run.id, run.note)}>{t('Raise defect')}</button>
            </div>
          )}
          {run?.result === 'fail' && runsWithDefect.has(run.id) && (
            <div style={{ marginTop: 6 }}>
              {bundle.defects.filter((d) => d.test_run_id === run.id).map((d) => (
                <span key={d.id} className="ac-lamp ac-lamp--fail" style={{ marginRight: 5 }}>{d.ref}</span>
              ))}
            </div>
          )}
          {/*
            N3 — the witnessed run, offered only on a test case that is READY. A draft test case is
            not yet a test: recording a result against one would put a witnessed PASS on a script
            still being written, and that row ends up inside a hashed acceptance record.
          */}
          {tc && tc.status === 'ready' && (
            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="btn btn-sm" onClick={() => { setRecordRun({ tc, result: 'pass' }); setRunNote(''); }}>
                {t('Record a run')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function openRaise(ac: PlanAc, tc: PlanTestCase | null, runId: number | null, note: string | null) {
    setRaise({ ac, tc, runId });
    setRaiseTitle(note ? note.slice(0, 120) : '');
    setRaiseDesc('');
    setRaiseSeverity('major');
    setRaiseFix('');
  }

  // ═════════════════════════════════════════════════════════════════════════
  // ZONE · DEFECTS
  // ═════════════════════════════════════════════════════════════════════════
  function renderDefectZone() {
    const rows = bundle.defects;
    return (
      <div className="ac-zone" id="ac-defects" data-anchor="ac-defects">
        <div className="zone-head">
          <span className="u-label">{t('Defect board')}</span>
          <span className="zone-note zone-note--end">
            {rows.length ? (
              <>
                <Fig>{st?.defects.open ?? 0}</Fig> {t('open')} <Sep />{' '}
                <Fig>{(st?.defects.blocking_open ?? 0) + (st?.defects.major_open ?? 0)}</Fig> {t('blocking or major')}
                {st?.defects.oldest_open_days !== null && st?.defects.oldest_open_days !== undefined && (
                  <> <Sep /> {t('oldest')} <Fig>{st.defects.oldest_open_days}</Fig> {t('days')}</>
                )}
              </>
            ) : <span className="n-none">{t('nothing raised')}</span>}
          </span>
        </div>

        {rows.length === 0 ? (
          <EmptyState
            kind="empty"
            title={t('No defect has been raised')}
            detail={
              <>
                {t('A defect must cite the acceptance criterion it violates — that constraint is in the schema, not the form. If nothing agreed was broken, the honest instrument is a change request.')}
                {(st?.reachability.acceptance_criteria.unpublished_refs.length ?? 0) > 0 && (
                  <>
                    {' '}{t('Right now the client cannot raise one either:')}{' '}
                    <Fig>{st?.reachability.acceptance_criteria.unpublished_refs.length}</Fig> {t('of')}{' '}
                    <Fig>{st?.reachability.acceptance_criteria.total}</Fig> {t('criteria are unpublished, so their form has nothing to select.')}
                  </>
                )}
              </>
            }
          >
            <button className="btn btn-sm" onClick={() => setZone('plan')}>{t('Open the plan to raise one from a failing run')}</button>
          </EmptyState>
        ) : (
          rows.map((d) => renderDefect(d))
        )}

        {bundle.severity && (
          <details className="ac-disc">
            <summary>
              {t('Severity definitions —')} <Ref>{bundle.severity.source}</Ref>{t(', the wording that goes in the record')}
            </summary>
            <div className="ac-sevdef">
              {bundle.severity.items.map((s) => (
                <div key={s.key} style={{ display: 'contents' }}>
                  <div><SeverityChip severity={s.key} /></div>
                  <div>{s.definition}</div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  }

  function renderDefect(d: Defect) {
    const off = OFF_BOARD.includes(d.status);
    const next = NEXT_STATE[d.status];
    const age = daysBetween(d.created_at, new Date().toISOString());
    const fixIn = d.fix_due_date ? daysBetween(new Date().toISOString(), d.fix_due_date) : null;
    return (
      <div className={`ac-defect ac-defect--${d.severity}${off ? ' ac-defect--off' : ''}`} key={d.id}>
        <div className="ac-defect-top">
          <span className="ref">{d.ref}</span>
          <SeverityChip severity={d.severity} />
          <span className="ac-status-word">{t(STATE_WORD[d.status])}</span>
          <span className="clock">
            {t('open')} <Fig>{age}</Fig> {t('days')} <Sep />{' '}
            {d.fix_due_date ? (
              <>
                {t('agreed fix')} <Fig>{day(d.fix_due_date)}</Fig>{' '}
                <span style={{ color: (fixIn ?? 0) < 0 ? 'var(--broken)' : 'var(--ink-4)' }}>
                  ({(fixIn ?? 0) < 0 ? t('{n}d past', { n: Math.abs(fixIn ?? 0) }) : t('{n}d out', { n: fixIn })})
                </span>
              </>
            ) : <span className="n-none">{t('no fix date agreed')}</span>}
          </span>
        </div>
        <div className="title">{d.title}</div>
        {d.description && <div className="desc">{d.description}</div>}
        {/* ATTEST §4: an evidence block is serif behind a 2px rule, CLOSED by the ref line. The
            old page opened an .ev-src with no .ev and left the criterion in sans. */}
        <Tx type="acceptance_criterion" id={d.acceptance_criterion_id} field="statement"
            original={d.acceptance_criterion_statement} className="ev" as="div" />
        <div className="ev-src">
          <span className="att att--quoted" aria-hidden="true" />
          <Ref>{d.acceptance_criterion_ref}</Ref><Sep /><Ref>{d.requirement_ref}</Ref>
          <span className="sep">—</span><span>{t('the criterion this violates')}</span>
          {d.test_case_ref && (
            <>
              <Sep />
              <span>{t('from')} <Ref>{d.test_case_ref}</Ref> {tCtx(t, 'run', 'done')} <Fig>{fmtDateTime(d.test_run_at)}</Fig></span>
            </>
          )}
          <Sep />
          <span>{t('raised by {who}', { who: d.raised_by ?? '—' })} <Fig>{day(d.created_at)}</Fig> ({t(d.raised_by_kind)})</span>
        </div>
        <div className="acts">
          {next && (
            <button className="btn btn-accent btn-sm" onClick={() => setAdvance({ defect: d, next })}>
              {t('Move to {state}', { state: t(STATE_WORD[next]) })}
            </button>
          )}
          {/* All three of these commit on change. */}
          <label className="ac-ctl">
            <span className="u-label">{t('Status')}</span>
            <select
              className="form-select" value={d.status} aria-label={t('Status of {ref}', { ref: d.ref })}
              onChange={(e) => {
                const value = e.target.value as DefectStatus;
                if (value === d.status) return;
                // N6: the statuses that change whether G6 passes get a confirm that names it.
                if (GATE_MOVING_STATUSES.includes(value)) setAdvance({ defect: d, next: value });
                else void patchDefect(d, { status: value }, t('{ref} moved to {state}', { ref: d.ref, state: t(STATE_WORD[value]) }));
              }}
            >
              {DEFECT_STATUSES.map((s) => <option key={s} value={s}>{t(STATE_WORD[s])}</option>)}
            </select>
          </label>
          <label className="ac-ctl">
            <span className="u-label">{t('Severity')}</span>
            <select
              className="form-select" value={d.severity} aria-label={t('Severity of {ref}', { ref: d.ref })}
              onChange={(e) => {
                const value = e.target.value as DefectSeverity;
                if (value !== d.severity) setSeverityChange({ defect: d, next: value });
              }}
            >
              {SEVERITIES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
            </select>
          </label>
          <label className="ac-ctl">
            <span className="u-label">{t('Agreed fix')}</span>
            <input
              type="date" className="form-input mono" aria-label={t('Agreed fix date on {ref}', { ref: d.ref })}
              value={d.fix_due_date ?? ''}
              onChange={(e) => void patchDefect(d, { fix_due_date: e.target.value || null }, t('Fix date on {ref} recorded', { ref: d.ref }))}
            />
          </label>
          <span className="spacer" />
          {!['withdrawn', 'closed'].includes(d.status) && (
            <button
              className="btn btn-sm"
              title={t('Not an AC violation after all — raise it as a change request instead')}
              onClick={() => { setConvertTarget(d); setConvertNote(''); }}
            >{t('To change request')}</button>
          )}
          <button
            className="btn btn-sm btn-ghost"
            title={t('Delete this defect — for a duplicate or a row raised by mistake')}
            onClick={() => void askDeleteDefect(d)}
          >{t('Delete')}</button>
        </div>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // ZONE · THE RECORD
  // ═════════════════════════════════════════════════════════════════════════
  function renderRecordZone() {
    const coverage = st?.records.coverage ?? [];
    return (
      <div className="ac-zone">
        {/* The task is a real operator_tasks row and belongs on the record. The CONTROL it names
            lives once, in the move at the top of the page — this card is its provenance. */}
        {(st?.tasks ?? []).map((task) => (
          <div className="ac-taskrow" key={task.id}>
            <div>
              <div className="u-label" style={{ color: 'var(--broken)', marginBottom: 6 }}>
                {t('Operator task')} <Sep /> <Ref>operator_tasks</Ref> <Sep /> {t('raised by the scheduler')}
              </div>
              <div style={{ fontSize: 'var(--t-scan)', color: 'var(--ink-1)' }}>
                {task.title}, <Fig>{task.age_days}</Fig> {t('days ago. The control is the one at the top of the page.')}
              </div>
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-3)', marginTop: 4, maxWidth: '78ch', lineHeight: 1.5 }}>
                {task.detail}
              </div>
            </div>
          </div>
        ))}

        <div className="zone ac-sec" id="ac-coverage" data-anchor="ac-coverage" style={{ marginTop: 6 }}>
          <div className="zone-head">
            <span className="u-label">{t('Deliverable coverage')}</span>
            <span className="zone-note zone-note--end">{t('G5 condition 2 — every deliverable must sit on an issued record')}</span>
          </div>
          {coverage.length === 0 ? (
            <EmptyState
              kind="empty"
              title={t('No deliverable is defined')}
              detail={t('G5 asks whether every deliverable sits on an issued acceptance record. With none defined there is nothing to cover, and the gate stays shut on its first condition.')}
            />
          ) : (
            <div className="ac-cov">
              {coverage.map((c) => (
                <div className="r" key={c.id}>
                  <span className={`ac-tick ${c.on_records.length ? 'ac-tick--met' : 'ac-tick--open'}`}>
                    {c.on_records.length ? '✓' : '·'}
                  </span>
                  <Ref>{c.ref}</Ref>
                  <span>{c.name}</span>
                  <span className="on">
                    {c.on_records.length
                      ? <>{t('on {refs} ({status})', { refs: c.on_records.map((r) => r.ref).join(', '), status: t(c.on_records[0]!.status) })}</>
                      : <span className="n-none">{t('on no issued record — G5 cannot clear')}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="zone ac-sec" id="ac-records" data-anchor="ac-records">
          <div className="zone-head">
            <span className="u-label">{t('Acceptance records')}</span>
            <span className="zone-note zone-note--end">
              {bundle.records.length
                ? <><Fig>{bundle.records.length}</Fig> {tp(bundle.records.length, 'record', 'records')} <Sep /> <Fig>{st?.records.issued ?? 0}</Fig> {t('awaiting the sponsor')}</>
                : <span className="n-none">{t('none composed')}</span>}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* One filled control per zone: when a task is open the move card at the top owns it. */}
            <button
              className={`btn ${(st?.tasks.length ?? 0) ? '' : 'btn-accent'}`}
              onClick={() => { setComposerOpen(true); void doPreview(); }}
            >{t('Compose a record')}</button>
          </div>

          {bundle.records.length === 0 ? (
            <EmptyState
              kind="empty"
              title={t('No acceptance record has been composed')}
              detail={
                <>
                  {t('A record is generated from the register: the deliverables covered, every test result, every open defect with its agreed fix date, the severity definitions the client signed up to, and your exclusions. Composing one now would print')}{' '}
                  <Fig>{(st?.runs.outstanding ?? 0)}</Fig> {t("tests with no result — which is exactly what the composer's warnings would say.")}
                </>
              }
            />
          ) : (
            bundle.records.map((ar) => renderRecord(ar))
          )}
        </div>
      </div>
    );
  }

  function renderRecord(ar: AcceptanceRecord) {
    const issuedDay = ar.issued_at ? ar.issued_at.slice(0, 10) : null;
    const deadline = ar.review_deadline;
    const today = new Date().toISOString().slice(0, 10);
    const left = deadline ? daysBetween(today, deadline) : null;
    const over = left !== null && left < 0 && ar.status === 'issued';
    // The track runs issued → max(deadline, today), so an overrun has LENGTH on the page instead
    // of a badge saying it happened.
    const window = issuedDay && deadline ? Math.max(1, daysBetween(issuedDay, deadline)) : null;
    const elapsed = issuedDay ? Math.max(0, daysBetween(issuedDay, today)) : null;
    const span = window !== null && elapsed !== null ? Math.max(window, elapsed, 1) : null;
    const pct = span && elapsed !== null ? Math.max(0, Math.min(100, Math.round((elapsed / span) * 100))) : null;
    const dlPct = span && window !== null ? Math.round((window / span) * 100) : null;

    const badge: [string, string] =
      ar.status === 'signed' ? ['ok', 'signed']
        : ar.status === 'issued' ? (over ? ['broken', 'window closed'] : ['attend', 'with the sponsor'])
          : ar.status === 'deemed_notice_issued' ? ['attend', 'deemed notice']
            : ar.status === 'withdrawn' ? ['', 'withdrawn'] : ['', 'draft'];

    return (
      <div className="ac-rec" key={ar.id}>
        <div className="ac-rec-head">
          <span className="ref">{ar.ref}</span>
          <span className="ms">{ar.milestone_name ?? t('— no milestone —')}</span>
          <span className="state">
            <span className={`ac-state-badge${badge[0] ? ` ac-state-badge--${badge[0]}` : ''}`}>{t(badge[1])}</span>
          </span>
        </div>
        <div className="ac-rec-body">
          {issuedDay && deadline && pct !== null && dlPct !== null ? (
            <div className="ac-clock">
              <div className="track">
                <div className="fill" style={{ width: `${Math.min(pct, dlPct)}%` }} />
                {over && <div className="fill fill--over" style={{ left: `${dlPct}%`, width: `${pct - dlPct}%` }} />}
                <div className="deadline" style={{ left: `calc(${dlPct}% - 1px)` }} />
                <div className={`pin${pct > 82 ? ' pin--right' : ''}`} style={{ left: `calc(${pct}% - 1px)` }}>
                  <span>{over ? t('{n}d overdue', { n: Math.abs(left ?? 0) }) : t('{n}d left', { n: left })}</span>
                </div>
              </div>
              <div className="ends">
                <span>{t('issued')} <b className="voice-figure">{day(issuedDay)}</b></span>
                <span>{t('window')} <Fig>{window}</Fig> {t('days')}</span>
                <span>{t('closes')} <b className="voice-figure">{day(deadline)}</b></span>
              </div>
              <div className="ac-court">
                {t('The ball is')} <strong>{t('with the sponsor')}</strong> {t('and has been for')} <Fig>{elapsed}</Fig> {t('days.')}{' '}
                {over
                  ? <>{t('The window closed')} <Fig>{Math.abs(left ?? 0)}</Fig> {t('days ago and Signet raised a task rather than accepting anything.')}</>
                  : t('Nothing here needs you until it closes.')}
              </div>
            </div>
          ) : (
            <div className="ac-court">
              {ar.status === 'draft'
                ? t('A draft: nothing has reached the client and no window is running. Issuing publishes it and starts the review clock.')
                : ar.status === 'withdrawn'
                  ? t('Withdrawn — unpublished from the portal and kept on the record (P4).')
                  : t('No review window is recorded on this record.')}
            </div>
          )}

          <div className="ac-grid2">
            <div className="ac-fact">
              <span className="u-label">{t('Deliverables covered')}</span>
              <span className="v">
                {ar.deliverables.length
                  ? ar.deliverables.map((d) => <span key={d.id}><Ref>{d.ref}</Ref>{' '}</span>)
                  : <span className="n-none">{t('none')}</span>}
              </span>
            </div>
            <div className="ac-fact">
              <span className="u-label">{t('Published to the portal')}</span>
              <span className="v">{ar.published ? t('yes') : <span className="n-none">{t('no')}</span>}</span>
            </div>
            <div className="ac-fact">
              <span className="u-label">{t('Signature')}</span>
              <span className="v">
                {ar.signature
                  ? <>{ar.signature.typed_name} <Fig>{fmtDateTime(ar.signature.signed_at)}</Fig></>
                  : <span className="n-none">{t('— unsigned')}</span>}
              </span>
            </div>
            <div className="ac-fact">
              <span className="u-label">{t('Content hash')}</span>
              <span className="v src-ref">{ar.content_hash ? `${ar.content_hash.slice(0, 24)}…` : '—'}</span>
            </div>
          </div>
        </div>
        <div className="ac-rec-acts">
          <button className="btn btn-sm" onClick={() => setViewRecord(ar)}>{t('Read the record')}</button>
          {ar.status === 'draft' && (
            <>
              <button
                className="btn btn-sm" disabled={busy}
                title={t('Re-run the composer over the current test results and defect board, keeping this ref')}
                onClick={() => void doRefreshRecord(ar)}
              >{t('Refresh')}</button>
              <button className="btn btn-sm btn-accent" onClick={() => { setIssueTarget(ar); setIssueDeadline(''); }}>
                {t('Issue')}
              </button>
            </>
          )}
          {ar.status === 'issued' && ar.window_expired && (
            <button className="btn btn-sm btn-danger" onClick={() => { setNoticeTarget(ar); setNoticeNote(''); }}>
              {t('Deemed notice')}
            </button>
          )}
          <span className="spacer" />
          {ar.status === 'draft' && (
            <button className="btn btn-sm btn-ghost" onClick={() => setDeleteTarget(ar)}>{t('Discard draft')}</button>
          )}
          {['issued', 'deemed_notice_issued'].includes(ar.status) && (
            <button className="btn btn-sm btn-ghost" onClick={() => setWithdrawTarget(ar)}>{t('Withdraw')}</button>
          )}
        </div>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // Floating layers — the only place --lift-* belongs (LAW 3)
  // ═════════════════════════════════════════════════════════════════════════

  function renderComposer() {
    const plan = bundle.plan;
    const options = composerOptions.length
      ? composerOptions
      : (plan?.plan ?? []).map((d) => ({ id: d.id, ref: d.ref, name: d.name, milestone_name: null as string | null }));
    const testRows = (plan?.plan ?? []).flatMap((d) =>
      d.requirements.flatMap((r) => r.acceptance_criteria.flatMap((ac) => ac.test_cases)),
    );
    const openBoard = bundle.defects.filter((d) => !OFF_BOARD.includes(d.status));

    return (
      <div className={`panel-overlay${composerOpen ? ' open' : ''}`}>
        <div className="panel-overlay-bg" onClick={() => setComposerOpen(false)} />
        <div className="panel-slide" style={{ width: 'min(700px, 96vw)' }}>
          {composerOpen && (
            <>
              <div className="panel-slide-head">
                <div>
                  <h3 className="panel-slide-title">{t('Compose an acceptance record')}</h3>
                  <div className="text-xs text-ink-3">{t('The document is generated from the register — it is not written by hand.')}</div>
                </div>
                <button className="panel-slide-close" onClick={() => setComposerOpen(false)}>✕</button>
              </div>
              <div className="panel-slide-body">
                {/* The warnings are the FIRST block, because they are printed inside the document
                    the sponsor reads. The old page fired them as toasts after the commit. */}
                {previewWarnings && previewWarnings.length > 0 && (
                  <div className="ac-warnblock">
                    <div className="u-label">{t('These sentences will be printed in the document')}</div>
                    {previewWarnings.map((w) => <p key={w}>{w}</p>)}
                    <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-meta)' }}>
                      {t('Composing is still allowed — a record that states its own qualifications is the point. But the sponsor reads them.')}
                    </p>
                  </div>
                )}
                {previewError && (
                  <EmptyState
                    kind="error"
                    title={t('The preview could not be rendered')}
                    detail={previewError}
                    onRetry={() => void doPreview()}
                  />
                )}

                <div className="form-group">
                  <label className="form-label" htmlFor="ar-milestone">{t('Milestone')}</label>
                  <select
                    id="ar-milestone" className="form-select" value={composerMilestone ?? ''}
                    onChange={(e) => { setComposerMilestone(e.target.value ? Number(e.target.value) : null); setComposerDeliverables([]); }}
                  >
                    <option value="">{t('— no milestone —')}</option>
                    {bundle.milestones.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}{m.due_date ? ` — ${t('due {date}', { date: fmtDate(m.due_date) })}` : ''} · {tp(m.deliverable_count, '{n} deliverable', '{n} deliverables')}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">{t('Deliverables covered')}</label>
                  {options.length === 0 && <div className="text-xs text-ink-3">{t("Preview to pull in the milestone's deliverables.")}</div>}
                  {options.map((d) => (
                    <label key={d.id} className="flex items-center gap-8 mb-4 text-sm cursor-pointer">
                      <input
                        type="checkbox" checked={composerDeliverables.includes(d.id)}
                        onChange={(e) => setComposerDeliverables((prev) => (e.target.checked ? [...prev, d.id] : prev.filter((x) => x !== d.id)))}
                      />
                      <Ref>{d.ref}</Ref> {d.name}
                      {d.milestone_name && <span className="text-xs text-ink-3">· {d.milestone_name}</span>}
                    </label>
                  ))}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="ar-exclusions">{t('Exclusions and qualifications')}</label>
                  <textarea
                    id="ar-exclusions" className="form-textarea" rows={4}
                    placeholder={t('e.g. Excludes the OCR pipeline (DEL-04) — deferred to phase 2 by agreement of 21.05.2026.')}
                    value={exclusions} onChange={(e) => setExclusions(e.target.value)}
                  />
                </div>

                <div className="u-label" style={{ marginBottom: 8 }}>{t('What the document will say')}</div>
                <div className="ac-doc">
                  <h4>{t('Deliverables covered')}</h4>
                  {options.filter((d) => composerDeliverables.includes(d.id)).length === 0
                    ? <div className="n-none">{t('None selected.')}</div>
                    : options.filter((d) => composerDeliverables.includes(d.id)).map((d) => (
                        <div key={d.id}><Ref>{d.ref}</Ref> {d.name}</div>
                      ))}
                  <h4>{t('Test results')}</h4>
                  {testRows.length === 0
                    ? <div className="n-none">{t('No test case is in scope.')}</div>
                    : testRows.map((tc) => (
                        <div key={tc.id}>
                          <Ref>{tc.ref}</Ref> {tc.title} — <b>{tc.latest_run ? t(tc.latest_run.result).toUpperCase() : t('NOT RUN')}</b>
                          {tc.latest_run ? <> <Sep /> <Fig>{fmtDateTime(tc.latest_run.run_at)}</Fig></> : null}
                        </div>
                      ))}
                  <h4>{t('Open defects')}</h4>
                  {openBoard.length === 0
                    ? <div>{t('None.')}</div>
                    : openBoard.map((d) => (
                        <div key={d.id}>
                          <Ref>{d.ref}</Ref> <b>{t(d.severity)}</b> {d.title}
                          {d.fix_due_date ? <> — {t('agreed fix')} <Fig>{day(d.fix_due_date)}</Fig></> : ` — ${t('no fix date agreed')}`}
                        </div>
                      ))}
                  <h4>{t('Severity definitions')}</h4>
                  <div>
                    {bundle.severity
                      ? <>{tp(bundle.severity.items.length, '{n} definition', '{n} definitions')} {t('from')} <Ref>{bundle.severity.source}</Ref>.</>
                      : <span className="n-unknown" title={t('the definitions did not load')}>{t('not read')}</span>}
                  </div>
                  <h4>{t('Footer')}</h4>
                  <div>{t('Signing accepts the deliverables listed above, subject to the open defects and their agreed fix dates.')}</div>
                </div>
              </div>
              <div className="ac-panel-foot">
                <div style={{ width: '100%', fontSize: 'var(--t-meta)', color: 'var(--ink-3)', marginBottom: 10 }}>
                  {t('Draft only — nothing reaches the client until you issue it.')}
                </div>
                <button className="btn" disabled={busy} onClick={() => void doPreview()}>{t('Re-read the register')}</button>
                <span className="spacer" />
                <button className="btn" onClick={() => setComposerOpen(false)}>{t('Cancel')}</button>
                <button className="btn btn-accent" disabled={busy} onClick={() => void doCreate()}>
                  {previewWarnings && previewWarnings.length ? t('Create the draft anyway') : t('Create the draft')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  function renderRaisePanel() {
    return (
      <div className={`panel-overlay${raise ? ' open' : ''}`}>
        <div className="panel-overlay-bg" onClick={() => setRaise(null)} />
        <div className="panel-slide" style={{ width: 'min(660px, 96vw)' }}>
          {raise && (
            <>
              <div className="panel-slide-head">
                <div>
                  <h3 className="panel-slide-title">{t('Raise a defect')}</h3>
                  <div className="text-xs text-ink-3">
                    {t('A defect must cite the acceptance criterion it violates. If nothing agreed was broken, it is a change request.')}
                  </div>
                </div>
                <button className="panel-slide-close" onClick={() => setRaise(null)}>✕</button>
              </div>
              <div className="panel-slide-body">
                <div className="u-label">{t('The criterion it violates — fixed by the failing run, not chosen from a list')}</div>
                <Tx type="acceptance_criterion" id={raise.ac.id} field="statement" original={raise.ac.statement}
                    className="ev" style={{ marginTop: 9 }} as="div" />
                <div className="ev-src">
                  <span className="att att--quoted" aria-hidden="true" />
                  <Ref>{raise.ac.ref}</Ref>
                  {raise.tc && <><Sep /><span>{t('from')} <Ref>{raise.tc.ref}</Ref></span></>}
                </div>

                <div className="form-group" style={{ marginTop: 18 }}>
                  <label className="form-label" htmlFor="def-title">{t('Title')}</label>
                  <input id="def-title" className="form-input" value={raiseTitle} onChange={(e) => setRaiseTitle(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="def-desc">{t('Description')}</label>
                  <textarea id="def-desc" className="form-textarea" rows={4} value={raiseDesc} onChange={(e) => setRaiseDesc(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="def-sev">{t('Severity')}</label>
                  <select id="def-sev" className="form-select" value={raiseSeverity} onChange={(e) => setRaiseSeverity(e.target.value as DefectSeverity)}>
                    {SEVERITIES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
                  </select>
                </div>
                {bundle.severity && (
                  <details className="ac-disc" open>
                    <summary>{t('What each severity commits you to')}</summary>
                    <div className="ac-sevdef">
                      {bundle.severity.items.map((s) => (
                        <div key={s.key} style={{ display: 'contents' }}>
                          <div><SeverityChip severity={s.key} /></div>
                          <div>{s.definition}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                <div className="form-group" style={{ marginTop: 16 }}>
                  <label className="form-label" htmlFor="def-fix">{t('Agreed fix date')}</label>
                  <input id="def-fix" type="date" className="form-input mono" value={raiseFix} onChange={(e) => setRaiseFix(e.target.value)} />
                </div>
              </div>
              <div className="ac-panel-foot">
                <button
                  className="btn"
                  onClick={() => { setRaise(null); void openRoute(ROUTES.contracting({ engagement: current!.id, tab: 'crs' })); }}
                >{t('This is new scope → change request')}</button>
                <span className="spacer" />
                <button className="btn" onClick={() => setRaise(null)}>{t('Cancel')}</button>
                <button
                  className="btn btn-accent"
                  disabled={busy || raiseTitle.trim().length < 5}
                  title={raiseTitle.trim().length < 5 ? t('A title of at least 5 characters is required — the server refuses less.') : undefined}
                  onClick={() => void doRaise()}
                >{t('Raise the defect')}</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  function renderModals() {
    const clockRecord = bundle.records.find((r) => r.id === st?.records.clock?.id) ?? null;
    return (
      <>
        {/* ── the record viewer ── */}
        <div className={`panel-overlay${viewRecord ? ' open' : ''}`}>
          <div className="panel-overlay-bg" onClick={() => setViewRecord(null)} />
          <div className="panel-slide" style={{ width: 'min(780px, 96vw)' }}>
            {viewRecord && (
              <>
                <div className="panel-slide-head">
                  <div>
                    <div className="src-ref text-sm text-ink-3">{viewRecord.ref} · {current!.code}</div>
                    <h3 className="panel-slide-title">{viewRecord.milestone_name ?? t('Acceptance record')}</h3>
                  </div>
                  <button className="panel-slide-close" onClick={() => setViewRecord(null)}>✕</button>
                </div>
                <div className="panel-slide-body">
                  <div className="text-xs text-ink-3 mb-8">
                    {t('The artefact itself — the HTML that is hashed, stored and signed. It is deliberately not restyled here.')}
                  </div>
                  <iframe
                    title={t('Acceptance record')} src={acceptanceApi.htmlUrl(viewRecord.id)}
                    style={{ width: '100%', height: 620, border: '1px solid var(--rule)', borderRadius: 'var(--r2)', background: '#fff' }}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── N2 · the readiness batch preflight ── */}
        <ConfirmModal
          open={readyOpen}
          title={t('Ready the plan for the client')}
          confirmLabel={t('Ready the plan')}
          onCancel={() => setReadyOpen(false)}
          onConfirm={() => void doReadyPlan({
            engagement_id: current!.id,
            open_matrix: batchRows.some((c) => c.id === 'matrix'),
            mark_ready: batchRows.some((c) => c.id === 'tc_ready'),
            publish_test_cases: batchRows.some((c) => c.id === 'tc_published'),
            publish_acs: batchRows.some((c) => c.id === 'ac_published'),
            publish_milestones: batchRows.some((c) => c.id === 'milestone'),
          })}
        >
          <p className="read">
            {t('This makes')} <strong>{batchRows.length}</strong> {tp(batchRows.length, 'write', 'writes')} {t("in one transaction. Every one is reversible from the Client Portal page; none of them touches the register's content.")}
          </p>
          <div className="ac-willwrite">
            <span className="u-label">{t('Will write')}</span>
            <ul>{batchRows.map((c) => <li key={c.id}>{c.writes}</li>)}</ul>
          </div>
          <div className="ac-willwrite" style={{ marginTop: 10 }}>
            <span className="u-label">{t('Will not touch')}</span>
            <ul>
              {heldRows.map((c) => (
                <li key={c.id}>
                  {c.what} — {c.id === 'portal'
                    ? t('engagements.portal_enabled and the invite: deciding who may see this engagement is a grant decision')
                    : t('nothing — whether a criterion is testable at all is a judgement, not a toggle')}
                </li>
              ))}
              <li>
                {t("The client's view moves along two axes at once: marking a test case ready and publishing it both change what the portal's UAT list counts. Nothing that is still draft will be published.")}
              </li>
            </ul>
          </div>
        </ConfirmModal>

        {/* ── N6 · the two confirms that name the consequence ── */}
        <ConfirmModal
          open={!!advance}
          title={advance ? t('Move {ref} to {state}', { ref: advance.defect.ref, state: t(STATE_WORD[advance.next]) }) : ''}
          confirmLabel={advance ? t('Move to {state}', { state: t(STATE_WORD[advance.next]) }) : t('Move')}
          onCancel={() => setAdvance(null)}
          onConfirm={() => {
            if (!advance) return;
            const { defect, next } = advance;
            setAdvance(null);
            void patchDefect(defect, { status: next }, t('{ref} moved to {state}', { ref: defect.ref, state: t(STATE_WORD[next]) }));
          }}
        >
          {advance && (
            <>
              <p className="read">
                {t('{ref} is', { ref: advance.defect.ref })} <strong>{t(STATE_WORD[advance.defect.status])}</strong> {t('and {severity}.', { severity: t(advance.defect.severity) })}{' '}
                {GATE_MOVING_STATUSES.includes(advance.next)
                  ? <>{t('Moving it to')} <strong>{t(STATE_WORD[advance.next])}</strong> {t('takes it OFF the blocking count —')} <span className="src-ref">G6</span> {t('may open as a result.')}</>
                  : <>{t('Moving it to')} <strong>{t(STATE_WORD[advance.next])}</strong> {t('does not clear')} <span className="src-ref">G6</span> {t('— only')} <em>{t('verified')}</em> {t('or')} <em>{t('closed')}</em> {t('takes it off the blocking count.')}</>}
              </p>
              <div className="ac-willwrite">
                <span className="u-label">{t('Will write')}</span>
                <ul>
                  <li><span className="src-ref">defects.status</span> {t('{from} → {to} on {ref}', { from: advance.defect.status, to: advance.next, ref: advance.defect.ref })}</li>
                  <li>{t('an audit row with the before and after')}</li>
                </ul>
              </div>
            </>
          )}
        </ConfirmModal>

        <ConfirmModal
          open={!!severityChange}
          title={severityChange ? t('Change {ref} to {severity}', { ref: severityChange.defect.ref, severity: t(severityChange.next) }) : ''}
          confirmLabel={t('Change the severity')}
          onCancel={() => setSeverityChange(null)}
          onConfirm={() => {
            if (!severityChange) return;
            const { defect, next } = severityChange;
            setSeverityChange(null);
            void patchDefect(defect, { severity: next }, t('{ref} is now {severity}', { ref: defect.ref, severity: t(next) }));
          }}
        >
          {severityChange && (
            <>
              <p className="read">
                {t('Severity is a commitment, not a label.')} <span className="src-ref">G6</span> {t('counts')}
                <strong> {t('blocking and major')}</strong> {t('defects that are not closed, verified or withdrawn — so')}
                {' '}{t(severityChange.defect.severity)} → {t(severityChange.next)}{' '}
                {['blocking', 'major'].includes(severityChange.next) && !['blocking', 'major'].includes(severityChange.defect.severity)
                  ? t('CLOSES the gate that may be open now.')
                  : !['blocking', 'major'].includes(severityChange.next) && ['blocking', 'major'].includes(severityChange.defect.severity)
                    ? t('OPENS a gate this defect is currently holding shut.')
                    : t('leaves the gate count unchanged.')}
              </p>
              <p className="read" style={{ marginTop: 10 }}>
                {t('The definition the client signed up to is the wording in the record, not this dropdown.')}
              </p>
            </>
          )}
        </ConfirmModal>

        {/* ── N3 · the operator-witnessed run ── */}
        <ConfirmModal
          open={!!recordRun}
          title={recordRun ? t('Record a witnessed run on {ref}', { ref: recordRun.tc.ref }) : ''}
          confirmLabel={t('Record the run')}
          onCancel={() => { setRecordRun(null); setRunNote(''); }}
          onConfirm={() => void doRecordRun()}
        >
          {recordRun && (
            <>
              <p className="read">
                {t('You watched this test run. The row is attributed to')} <strong>{tCtx(t, 'you', 'dative')}</strong> {t('as an operator-witnessed run — the runs table renders')}{' '}
                <span className="src-ref">run_by_kind</span>{t(', so it can never be mistaken for one the client executed. That distinction is the evidentiary value; it ends up inside a hashed record.')}
              </p>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label" htmlFor="run-result">{t('Result')}</label>
                <select
                  id="run-result" className="form-select" value={recordRun.result}
                  onChange={(e) => setRecordRun({ ...recordRun, result: e.target.value as TestResult })}
                >
                  <option value="pass">{t('pass')}</option>
                  <option value="fail">{t('fail')}</option>
                  <option value="blocked">{t('blocked')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="run-note">{t('What you observed')}</label>
                <textarea
                  id="run-note" className="form-textarea" rows={3}
                  placeholder={t('e.g. Walked section 4 with Asbjorn on 5 August; all six headings present.')}
                  value={runNote} onChange={(e) => setRunNote(e.target.value)}
                />
              </div>
            </>
          )}
        </ConfirmModal>

        {/* ── issue ── */}
        <ConfirmModal
          open={!!issueTarget}
          title={issueTarget ? t('Issue {ref}', { ref: issueTarget.ref }) : ''}
          confirmLabel={t('Issue to the client')}
          onCancel={() => setIssueTarget(null)}
          onConfirm={() => void doIssue()}
        >
          <p className="read">
            {t('Issuing publishes the record to the portal and starts the review window. Leave the date empty to use')}{' '}
            <span className="src-ref">{current!.code}</span>{t("'s review window of")}{' '}
            <Fig>{st?.engagement.review_window_days ?? '—'}</Fig> {t('working days.')}
          </p>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label" htmlFor="ar-deadline">{t('Review deadline (optional)')}</label>
            <input id="ar-deadline" type="date" className="form-input mono" value={issueDeadline} onChange={(e) => setIssueDeadline(e.target.value)} />
          </div>
          <p className="text-xs text-ink-3">{t('On expiry Signet raises an operator task. It never accepts anything on its own.')}</p>
        </ConfirmModal>

        {/* ── the deemed-acceptance notice ── */}
        <ConfirmModal
          open={!!noticeTarget}
          title={noticeTarget ? t('Deemed-acceptance notice — {ref}', { ref: noticeTarget.ref }) : ''}
          danger
          confirmLabel={t('Issue the notice')}
          onCancel={() => setNoticeTarget(null)}
          onConfirm={() => void doNotice()}
        >
          {noticeTarget && (
            <>
              <p className="read">
                {t('The review window closed')} <Fig>{day(noticeTarget.review_deadline)}</Fig> {t('with no signature. The notice is appended to the record and its hash re-computed; the sponsor can still counter-sign afterwards.')}
              </p>
              <div className="ac-willwrite" style={{ marginTop: 12 }}>
                <span className="u-label">{t('Will write')}</span>
                <ul>
                  <li>{t('the notice text into')} <span className="src-ref">acceptance_records.content_html</span>{t(', hash re-computed')}</li>
                  <li><span className="src-ref">status</span> → <span className="src-ref">deemed_notice_issued</span></li>
                  <li>{t('the milestone and its deliverables → accepted')}</li>
                  <li>{t('the operator task closed')}</li>
                </ul>
              </div>
              {(st?.defects.blocking_open ?? 0) + (st?.defects.major_open ?? 0) > 0 && (
                <div className="ac-warnblock" style={{ marginTop: 14 }}>
                  <div className="u-label">{t('Read this first')}</div>
                  <p>
                    {t('The document still lists')}{' '}
                    <Fig>{(st?.defects.blocking_open ?? 0) + (st?.defects.major_open ?? 0)}</Fig>{' '}
                    {tp((st?.defects.blocking_open ?? 0) + (st?.defects.major_open ?? 0), 'open blocking or major defect', 'open blocking or major defects')}
                    {st?.defects.worst ? <> (<span className="src-ref">{st.defects.worst.ref}</span>)</> : null}. {t('The notice says the deliverable is treated as accepted')}{' '}
                    <em>{t('subject to')}</em> {t('that defect and its agreed fix date. If that is not the position you want on the record, close the defect first and recompose.')}
                  </p>
                </div>
              )}
              <div className="form-group" style={{ marginTop: 14 }}>
                <label className="form-label" htmlFor="notice-note">{t('Note for the record (optional)')}</label>
                <textarea
                  id="notice-note" className="form-textarea" rows={3}
                  placeholder={t('e.g. Called the sponsor on 5 August; no objection raised.')}
                  value={noticeNote} onChange={(e) => setNoticeNote(e.target.value)}
                />
              </div>
            </>
          )}
        </ConfirmModal>

        {/* ── withdraw / discard ── */}
        <ConfirmModal
          open={!!withdrawTarget}
          title={t('Withdraw {ref}?', { ref: withdrawTarget?.ref ?? '' })}
          danger confirmLabel={t('Withdraw')}
          onCancel={() => setWithdrawTarget(null)}
          onConfirm={() => void doWithdrawRecord()}
        >
          <p className="read">
            {t('The record is unpublished from the portal and kept as')} <span className="src-ref">withdrawn</span> {t('— it is never deleted (P4). Use this when the facts moved after issue: withdraw, compose a fresh record, issue that.')}
          </p>
        </ConfirmModal>

        <ConfirmModal
          open={!!deleteTarget}
          title={t('Discard draft {ref}?', { ref: deleteTarget?.ref ?? '' })}
          danger confirmLabel={t('Discard')}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void doDeleteRecord()}
        >
          <p className="read">
            {t('This draft was never issued, so the client has never seen it and nothing leaves the record. If it is only out of date,')} <strong>{t('Refresh')}</strong> {t('recomposes it against the current facts and keeps the ref.')}
          </p>
        </ConfirmModal>

        {/* ── convert to CR ── */}
        <ConfirmModal
          open={!!convertTarget}
          title={t('Convert {ref} to a change request', { ref: convertTarget?.ref ?? '' })}
          confirmLabel={t('Convert')}
          onCancel={() => setConvertTarget(null)}
          onConfirm={() => void runConvert()}
        >
          <p className="read">
            {t('The defect is withdrawn (never deleted) with a link note, and a draft change request is created carrying its description. Use this when nothing agreed was actually broken.')}
          </p>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label" htmlFor="convert-note">{t('Triage note')}</label>
            <textarea
              id="convert-note" className="form-textarea" rows={3}
              placeholder={t('e.g. Column order was never in an acceptance criterion — this is new scope.')}
              value={convertNote} onChange={(e) => setConvertNote(e.target.value)}
            />
          </div>
        </ConfirmModal>

        {/* ── the defect delete: the plan arrives BEFORE the dialog ── */}
        {defectDelete && defectDelete.plan.allowed && (
          <ConfirmModal
            open
            title={t('Delete {ref}?', { ref: defectDelete.defect.ref })}
            danger
            confirmLabel={defectDeleteBusy ? t('Deleting…') : t('Delete permanently')}
            onCancel={() => setDefectDelete(null)}
            onConfirm={() => void doDeleteDefect()}
          >
            <p className="text-sm text-ink-3 mb-8">{defectDelete.plan.label}</p>
            <CascadeList plan={defectDelete.plan} />
            <p className="text-xs text-ink-3 mt-12">
              {t('Immediate and permanent — this is for a defect that should never have been raised (a duplicate of the same failing run, a test row). If it was real and simply is not a defect after all, set it to')}{' '}
              <span className="src-ref">withdrawn</span> {t('or convert it to a change request: both keep the triage decision on the record. The audit log keeps the deleted row.')}
            </p>
          </ConfirmModal>
        )}
        {defectDelete && !defectDelete.plan.allowed && (
          <BlockedDeleteModal
            title={t('{ref} cannot be deleted', { ref: defectDelete.defect.ref })}
            plan={defectDelete.plan}
            onClose={() => setDefectDelete(null)}
          />
        )}

        {/* ── preview as client ── */}
        <ConfirmModal
          open={previewAsClient}
          title={t('Preview as client')}
          confirmLabel={runnable === tcTotal && tcTotal > 0 ? t('Open the Client Portal page') : t('Open the Client Portal page')}
          onCancel={() => setPreviewAsClient(false)}
          onConfirm={() => { setPreviewAsClient(false); void openRoute(ROUTES.portalAdmin({ engagement: current!.id })); }}
        >
          {runnable === tcTotal && tcTotal > 0 ? (
            <p className="read">
              {t('The portal is open and the plan is reachable:')} <Fig>{tcTotal}</Fig> {t('test cases,')}{' '}
              <Fig>{st?.reachability.acceptance_criteria.published ?? 0}</Fig> {t('published criteria and the defect form. The preview link itself is minted on the Client Portal page, which is where the grant it bypasses is administered.')}
            </p>
          ) : (
            <p className="read">
              {t('The portal is')} {st?.reachability.portal_enabled ? tCtx(t, 'on', 'state') : <strong>{t('off')}</strong>} {t('for')}{' '}
              <span className="src-ref">{current!.code}</span> {t('and')}{' '}
              <Fig>{st?.reachability.portal_users ?? 0}</Fig> {t('people are invited. A preview would show an empty shell — which is accurate, and is the point of step 1.')}
            </p>
          )}
        </ConfirmModal>
      </>
    );
  }
}
